<template>
  <div
    class="ns-search-results"
    data-testid="searchTerm"
    :data-searchTerm="query"
  >
    <div class="crop-container">
      <div v-if="hasResults">
        <NsProductListSortable
          :select-placeholder="(translations.productCard || {}).select"
          :skus="skus"
          :sku-search-scores="skusWithScores"
          :sort-by-label="translations.sortBy"
          :sort-by-options="translations.sortOptions"
          :sort-by-selected="sortBySelected"
          @sort-by-select="handleSortBySelect"
        >
          <div slot="woc-heading" class="woc-heading">
            <div class="back-button" @click="back">
              <NsIcon class="back-button-icon" icon-name="icon-angle-left-24" />
              <span class="back-button-text">{{ translations.back }}</span>
            </div>
            <span class="search-results-text" v-html="searchResultsText"></span>
          </div>
        </NsProductListSortable>
      </div>

      <div v-if="!hasResults" class="no-results-body">
        <div class="img-container">
          <img :src="localNoResultsImageUrl" alt="No Results" />
        </div>
        <p class="no-results-text" v-html="translations.noResults"></p>
        <p class="search-query">"{{ query }}"</p>
      </div>
    </div>

    <div v-if="!hasResults" class="no-results-footer">
      <NsProductCarousel v-if="showSearchResults" type="popular" />
    </div>
  </div>
</template>

<script>
import { UserService } from "@nuskin/ns-account";
import { Product, ProductStatusService } from "@nuskin/ns-shop";
import {
  events,
  ConfigService,
  SitesSessionStorageService
} from "@nuskin/ns-util";
import { NsIcon } from "@nuskin/design-components";
import {
  NsProductListSortable,
  NsProductCarousel
} from "@nuskin/product-components";
import { getProp } from "@nuskin/ns-common-lib";

const SEARCH_TERM_KEY = "searchResultsData";

export default {
  name: "NsSearchResults",
  components: { NsIcon, NsProductListSortable, NsProductCarousel },
  props: {
    showSearchResults: Boolean,
    translations: {
      type: Object,
      default: () => ({})
    },
    noResultsImageUrl: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      marketConfig: ConfigService.getMarketConfig(),
      query: "",
      searchResults: [],
      searchResultsText: "",
      sortBySelected: "bestMatch"
    };
  },
  computed: {
    resultsSkus() {
      const searchResult =
        !!this.searchResults && this.searchResults.length > 0
          ? this.searchResults[0]
          : undefined;
      return searchResult ? searchResult.resultsList || [] : [];
    },
    hasResults() {
      return this.resultsSkus.length > 0;
    },
    skusWithScores() {
      return this.resultsSkus.reduce((result, product) => {
        result[product.sku] = product.searchScore;
        return result;
      }, {});
    },
    skus() {
      return this.hasResults ? Object.keys(this.skusWithScores) : [];
    },
    localNoResultsImageUrl() {
      return (
        this.noResultsImageUrl ||
        `${this.marketConfig.siteUrl}/etc/designs/hello/packaged/img/noResults.png`
      );
    }
  },
  mounted() {
    if (this.showSearchResults) {
      this.setResultsFromLocalStorage();
    }

    events.subscribe(events.shop.SEARCH_EXECUTED, this.handleSearchExecuted);
  },
  destroyed() {
    events.unsubscribe(events.shop.SEARCH_EXECUTED, this.handleSearchExecuted);
  },
  methods: {
    handleSortBySelect(selected) {
      this.sortBySelected = selected;
    },

    back() {
      this.$emit("back-button-clicked");
    },

    resultsUpdated() {
      this.updateSearchForText();
      var skus = [];
      if (this.hasResults) {
        for (var i = 0; i < this.searchResults[0].resultsList.length; i++) {
          this.searchResults[0].resultsList[i] = new Product(
            this.searchResults[0].resultsList[i]
          );
          skus.push(this.searchResults[0].resultsList[i].sku);
        }

        this.getProductStatusFromService(skus);
      } else {
        events.publish("closeSpinner");
      }

      this.$emit("scroll-to-top");
    },

    getProductStatusFromService(skus) {
      const user = UserService.getUser();
      ProductStatusService.getProductStatus(skus).then(productStatuses => {
        for (let p = 0; p < productStatuses.length; p++) {
          for (let i = 0; i < this.searchResults[0].resultsList.length; i++) {
            if (
              this.searchResults[0].resultsList[i].sku ===
              productStatuses[p].SKU
            ) {
              this.searchResults[0].resultsList[i].addPricingFromStatus(
                productStatuses[p],
                typeof user !== "undefined" && user ? user.priceType : "WRTL"
              );
              break;
            }
          }
        }
        this.saveResultsToLocalStorage();
        events.publish("closeSpinner");
      });
    },

    updateSearchForText() {
      const SEARCH_TERM = "%searchTerm%";
      const SEARCH_RESULTS_NUMBER = "%searchResultsNumber%";

      this.searchResultsText = this.t("searchResultsFor", "")
        .replace(SEARCH_TERM, '<span id="query">"' + this.query + '"</span>')
        .replace(SEARCH_RESULTS_NUMBER, this.resultsSkus.length);
    },

    saveResultsToLocalStorage() {
      let searchResults = SitesSessionStorageService.getItem(SEARCH_TERM_KEY);
      searchResults = !searchResults ? {} : JSON.parse(searchResults);

      if (this.query) {
        searchResults.query = this.query;
      }

      if (this.searchResults && this.searchResults.length > 0) {
        searchResults.searchResults = this.searchResults;
      }

      SitesSessionStorageService.writeItem(
        SEARCH_TERM_KEY,
        JSON.stringify(searchResults)
      );
    },

    setResultsFromLocalStorage() {
      let resultsData = SitesSessionStorageService.getItem(SEARCH_TERM_KEY);
      if (resultsData) {
        resultsData = JSON.parse(resultsData);
        this.query = resultsData.query;
        this.searchResults = resultsData.searchResults;
      }
    },

    handleSearchExecuted(searchObject) {
      if (searchObject && searchObject.showSearchResults) {
        this.sortBySelected = "bestMatch";
        this.query = searchObject.query || "";
        this.searchResults = searchObject.resultsObject || [];

        if (this.searchResults && this.searchResults.length) {
          this.resultsUpdated();
        }
      }
    },

    t(key, defaultVal) {
      return getProp(this.translations, key, defaultVal);
    }
  }
};
</script>

<style lang="scss" scoped>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/global.mixins";
@use "~@nuskin/ns-core-styles/src/scss/colors";

* {
  box-sizing: border-box;
}

.ns-search-results,
.crop-container {
  position: relative;
  width: 100%;
}

.no-results-body {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 130px;
  text-align: center;
}

.img-container,
.no-results-text,
.search-query {
  width: 400px;
  max-width: 100%;
}

.no-results-text {
  margin-bottom: 10px;
}

.search-query {
  font-weight: 600;
}

.img-container img {
  width: 400px;
  max-width: 100%;
}

.no-results-footer {
  border-top: 1px solid map.get(colors.$themeColors, color-gray-3);
  width: 100%;
  background-color: #fff;
}

.woc-heading {
  display: flex;
  align-items: center;
}

.back-button {
  display: flex;
  color: map.get(colors.$themeColors, color-interactive-7);
  white-space: nowrap;
  cursor: pointer;

  .back-button-icon {
    margin-top: -1px;
    height: 20px;
    width: 20px;
  }

  .back-button-text {
    height: 20px;
    @include global.a-bold-caps;
    line-height: 22px;
    border-bottom: 1px solid transparent;
  }

  &:hover {
    .back-button-text {
      border-bottom: 1px solid currentColor;
    }
  }
}

.search-results-text {
  display: inline-block;
  margin-left: 20px;
}

@media (min-width: 1440px) {
  .crop-container {
    width: 1410px;
    margin: 0 auto;
  }
}
</style>
